import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { MenuDataItem } from '@ant-design/pro-components';
import { history, setLocale, useAntdConfigSetter, useIntl } from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  TenantSwitchModal,
  useSession,
  useShell,
  useTenant,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import {
  AppstoreOutlined,
  AreaChartOutlined,
  BarChartOutlined,
  CalendarOutlined,
  TeamOutlined,
  HomeOutlined,
  RollbackOutlined,
  DashboardOutlined,
  SettingOutlined,
  BankOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { App, theme as antdTheme } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import { getBootstrapTenantList, sessionAdapter } from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  Footer,
  Question,
  SelectLang,
  ThemeToggle,
} from '@/components';
import AntdAppBridge from '@/components/antdAppBridge';
import OpenPlatformNotificationIcon from '@/components/OpenPlatformNotificationIcon';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import {
  getProjectDetail,
  getMyAuthorizedResources,
  queryDictionaryCatalog,
} from '@/services/bff';
import OpenPlatformBootstrap from '@/components/OpenPlatformBootstrap';
import { buildAuthorizedResourceAccessMap, type AuthorizedResourceAccessMap } from '@/permissions/adapter';
import { hasResourceAccess } from '@/permissions/helpers';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import { setDictionaryCatalog } from '@/dictionaries';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import { getProjectDetailShellState } from '@/pages/project-center/project/detail/shellState';
import type { ProjectDetailNavKey } from '@/pages/project-center/project/detail/shared';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import resolveAccess from './access';

type AppInitialState = {
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: (API.CurrentUser & CurrentUser) | null;
  loading?: boolean;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
};

type RuntimeLayoutConfig = (context: {
  initialState?: AppInitialState;
  setInitialState: React.Dispatch<React.SetStateAction<AppInitialState | undefined>>;
}) => Record<string, unknown>;

export const request: Record<string, unknown> = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback', '/user/access-denied'];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth() ?? 208,
});

type InitialStateUpdater = React.Dispatch<
  React.SetStateAction<AppInitialState | undefined>
>;

type MenuTreeConfig = {
  key: string;
  name: string;
  path?: string;
  icon?: string;
  resourceCode?: string;
  children?: MenuTreeConfig[];
};

type MenuTranslator = (key: string) => string;

const PROJECT_CENTER_MENU_CONFIGS: MenuTreeConfig[] = [
  {
    key: 'workbench',
    name: 'menu.project-center.workbench',
    path: '/project-center/workbench',
    icon: 'home',
    resourceCode: 'project-workbench',
  },
  {
    key: 'project-overview-group',
    name: 'menu.project-center.project-overview',
    icon: 'project',
    children: [
      {
        key: 'projects',
        name: 'menu.project-center.projects',
        path: '/project-center/projects',
        icon: 'project',
        resourceCode: 'project-list',
      },
      {
        key: 'boards',
        name: 'menu.project-center.boards',
        path: '/project-center/boards',
        icon: 'partition',
        resourceCode: 'global-boards',
      },
      {
        key: 'gantt',
        name: 'menu.project-center.gantt',
        path: '/project-center/gantt',
        icon: 'barChart',
        resourceCode: 'global-gantt',
      },
      {
        key: 'resources',
        name: 'menu.project-center.resources',
        path: '/project-center/resources',
        icon: 'team',
        resourceCode: 'resource-center',
      },
      {
        key: 'calendar',
        name: 'menu.project-center.calendar',
        path: '/project-center/calendar',
        icon: 'calendar',
        resourceCode: 'calendar',
      },
    ],
  },
  {
    key: 'analytics',
    name: 'menu.project-center.analytics',
    path: '/project-center/analytics',
    icon: 'areaChart',
    resourceCode: 'analytics',
  },
  {
    key: 'risks',
    name: 'menu.project-center.risks',
    path: '/project-center/risks',
    icon: 'warning',
    resourceCode: 'global-risks',
  },
  {
    key: 'dashboard',
    name: 'menu.project-center.dashboard',
    path: '/project-center/dashboard',
    icon: 'dashboard',
    resourceCode: 'project-dashboard',
  },
  {
    key: 'system',
    name: 'menu.system',
    icon: 'setting',
    children: [
      {
        key: 'permissions',
        name: 'menu.system.permissions',
        path: '/project-center/system/permissions',
        resourceCode: 'permission',
      },
      {
        key: 'dictionary',
        name: 'menu.system.dictionary',
        path: '/project-center/system/dictionaries',
        resourceCode: 'dictionary',
      },
      {
        key: 'ai-model-binding',
        name: 'menu.system.ai-model-binding',
        path: '/project-center/system/ai-model-binding',
        resourceCode: 'ai-model-binding',
      },
    ],
  },
];

const PROJECT_SPACE_MENU_CONFIGS: MenuTreeConfig = {
  key: 'project-space',
  name: 'menu.project-center.project-space',
  icon: 'project',
  children: [
    {
      key: 'overview',
      name: 'menu.project-center.project-detail.overview',
    path: '/project-center/projects/:projectUuid/overview',
    resourceCode: 'project-overview',
    },
    {
      key: 'gantt',
      name: 'menu.project-center.project-detail.gantt',
    path: '/project-center/projects/:projectUuid/gantt',
    resourceCode: 'project-gantt',
    },
    {
      key: 'kanban',
      name: 'menu.project-center.project-detail.kanban',
    path: '/project-center/projects/:projectUuid/kanban',
    resourceCode: 'project-kanban',
    },
    {
      key: 'delivery',
      name: 'menu.project-center.project-detail.delivery',
    path: '/project-center/projects/:projectUuid/delivery',
    resourceCode: 'project-file-contract',
    },
    {
      key: 'communication',
      name: 'menu.project-center.project-detail.communication',
    path: '/project-center/projects/:projectUuid/communication',
    resourceCode: 'project-communication',
    },
    {
      key: 'acceptance',
      name: 'menu.project-center.project-detail.acceptance',
    path: '/project-center/projects/:projectUuid/acceptance',
    resourceCode: 'project-acceptance',
    },
    {
      key: 'issues',
      name: 'menu.project-center.project-detail.issues',
    path: '/project-center/projects/:projectUuid/issues',
    resourceCode: 'project-issues',
    },
    {
      key: 'report',
      name: 'menu.project-center.project-detail.report',
    path: '/project-center/projects/:projectUuid/report',
    resourceCode: 'project-reports',
    },
    {
      key: 'review',
      name: 'menu.project-center.project-detail.review',
    path: '/project-center/projects/:projectUuid/review',
    resourceCode: 'project-review',
    },
    {
      key: 'members',
      name: 'menu.project-center.project-detail.members',
    path: '/project-center/projects/:projectUuid/members',
    resourceCode: 'project-members',
    },
    {
      key: 'work-hours',
      name: 'menu.project-center.project-detail.work-hours',
    path: '/project-center/projects/:projectUuid/work-hours',
    resourceCode: 'project-work-hours',
    },
    {
      key: 'activities',
      name: 'menu.project-center.project-detail.activities',
    path: '/project-center/projects/:projectUuid/activities',
    resourceCode: 'project-activities',
    },
  ],
};

const getMenuIcon = (icon?: string) => {
  switch (icon) {
    case 'home':
      return <HomeOutlined />;
    case 'project':
      return <AppstoreOutlined />;
    case 'partition':
      return <AppstoreOutlined />;
    case 'barChart':
      return <BarChartOutlined />;
    case 'team':
      return <TeamOutlined />;
    case 'calendar':
      return <CalendarOutlined />;
    case 'areaChart':
      return <AreaChartOutlined />;
    case 'warning':
      return <WarningOutlined />;
    case 'dashboard':
      return <DashboardOutlined />;
    case 'setting':
      return <SettingOutlined />;
    default:
      return undefined;
  }
};

const buildMenuTree = (
  configs: MenuTreeConfig[],
  translateMenuName: MenuTranslator,
  authorizedResourceAccess: AuthorizedResourceAccessMap,
  pathParams?: Record<string, string>,
): MenuDataItem[] => {
  return configs
    .map((item) => {
      const children = item.children
        ? buildMenuTree(item.children, translateMenuName, authorizedResourceAccess, pathParams)
        : undefined;
      const path = item.path?.replace(
        ':projectUuid',
        pathParams?.projectUuid || '',
      );

      if (item.children?.length) {
        if (!children?.length) {
          return null;
        }
        return {
          key: item.key,
          name: translateMenuName(item.name),
          icon: getMenuIcon(item.icon),
          path,
          children,
          hideInMenu: false,
          locale: false,
        } as MenuDataItem;
      }

      if (!path) {
        return null;
      }

      if (item.resourceCode && !hasResourceAccess(authorizedResourceAccess, item.resourceCode)) {
        return null;
      }

      return {
        key: item.key,
        name: translateMenuName(item.name),
        icon: getMenuIcon(item.icon),
        path,
        hideInMenu: false,
        locale: false,
      } as MenuDataItem;
    })
    .filter(Boolean) as MenuDataItem[];
};

const getVisibleProjectSpaceMenuItems = (
  authorizedResourceAccess: AuthorizedResourceAccessMap,
  pathParams?: Record<string, string>,
) => {
  const children = buildMenuTree(
    PROJECT_SPACE_MENU_CONFIGS.children || [],
    (key) => key,
    authorizedResourceAccess,
    pathParams,
  );

  if (!children.length) {
    return [];
  }

  return [
    {
      key: PROJECT_SPACE_MENU_CONFIGS.key,
      name: PROJECT_SPACE_MENU_CONFIGS.name,
      icon: getMenuIcon(PROJECT_SPACE_MENU_CONFIGS.icon),
      children,
      hideInMenu: false,
      locale: false,
    } as MenuDataItem,
  ];
};

const PROJECT_DETAIL_PATH_PATTERN =
  /^\/project-center\/projects\/([^/]+)(?:\/([^/]+))?\/?$/;

const isProjectDetailPath = (pathname: string) =>
  PROJECT_DETAIL_PATH_PATTERN.test(pathname);

const getProjectDetailRouteInfo = (pathname: string) => {
  const matched = pathname.match(PROJECT_DETAIL_PATH_PATTERN);
  if (!matched?.[1]) {
    return null;
  }

  return {
    projectUuid: matched[1],
    section: matched[2] as ProjectDetailNavKey | undefined,
  };
};

const getProjectDetailMenuData = (
  pathname: string,
  authorizedResourceAccess: AuthorizedResourceAccessMap,
  translateMenuName: MenuTranslator = (key) => key,
): MenuDataItem[] | null => {
  const matched = pathname.match(PROJECT_DETAIL_PATH_PATTERN);

  if (!matched?.[1]) {
    return null;
  }

  const projectUuid = matched[1];
  return getVisibleProjectSpaceMenuItems(authorizedResourceAccess, { projectUuid })[0]
    ?.children?.map((item) => ({
      ...item,
      name: translateMenuName(item.name as string),
      locale: false,
    })) || [];
};

const ProjectDetailHeaderBanner: React.FC<{ pathname: string }> = ({
  pathname,
}) => {
  const routeInfo = getProjectDetailRouteInfo(pathname);
  const shellState = getProjectDetailShellState();
  const [projectName, setProjectName] = React.useState('');

  React.useEffect(() => {
    let active = true;

    if (!routeInfo?.projectUuid) {
      setProjectName('');
      return () => {
        active = false;
      };
    }

    if (
      shellState?.projectUuid === routeInfo.projectUuid &&
      shellState.projectName
    ) {
      setProjectName(shellState.projectName);
      return () => {
        active = false;
      };
    }

    void (async () => {
      try {
        const detail = await getProjectDetail(routeInfo.projectUuid);
        if (active) {
          setProjectName(detail.name || routeInfo.projectUuid);
        }
      } catch {
        if (active) {
          setProjectName(routeInfo.projectUuid);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [routeInfo?.projectUuid, shellState?.projectName, shellState?.projectUuid]);

  if (!routeInfo) {
    return null;
  }

  return (
    <div className="app-header-detail-banner-shell">
      <fieldset className="app-header-detail-banner" aria-label="当前项目">
        <button
          type="button"
          className="app-header-detail-back-btn"
          onClick={() => history.push('/project-center/projects')}
        >
          <RollbackOutlined />
          <span className="app-header-detail-back-text">返回</span>
        </button>
        <span className="app-header-detail-banner-divider" />
        <span className="app-header-detail-banner-name" title={projectName}>
          {projectName || routeInfo.projectUuid}
        </span>
      </fieldset>
    </div>
  );
};

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        appId: BizAppConfig.appSsoClientId,
        personalCenterUrl: BizAppConfig.appPersonalCenterUrl,
        tenantManagementUrl: BizAppConfig.appTenantManagementUrl,
        workbenchUrl: BizAppConfig.appWorkbenchUrl,
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.replace(applicationReturnTo);
        return;
      }
      history.push('/project-center');
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle, workbenchUrl }) => {
  const intl = useIntl();
  const workbenchLabel = intl.formatMessage({ id: 'menu.account.workbench' });

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
      <button
        type="button"
        className="app-header-workbench-btn"
        onClick={() => {
          if (!workbenchUrl) {
            return;
          }
          window.open(workbenchUrl, '_self');
        }}
        disabled={!workbenchUrl}
        title={workbenchLabel}
      >
        <HomeOutlined />
        <span className="app-header-workbench-text">{workbenchLabel}</span>
      </button>
    </div>
  );
};

const CurrentTenant: React.FC = () => {
  const { currentTenantUuid, tenants } = useTenant();
  const [tenantModalOpen, setTenantModalOpen] = React.useState(false);
  const currentTenant = tenants.find(
    (tenant) => tenant.id === currentTenantUuid,
  );

  if (!currentTenant) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="app-current-tenant"
        title={currentTenant.name}
        aria-label={`切换企业，当前：${currentTenant.name}`}
        onClick={() => setTenantModalOpen(true)}
      >
        <span className="app-current-tenant-icon" aria-hidden="true">
          <BankOutlined />
        </span>
        <span className="app-current-tenant-name">{currentTenant.name}</span>
      </button>
      <TenantSwitchModal
        open={tenantModalOpen}
        onClose={() => setTenantModalOpen(false)}
        labels={{
          title: '切换企业',
          empty: '暂无可切换企业',
          switchAction: '切换企业',
          createAction: '创建/加入企业',
          deleteAction: '删除企业',
        }}
      />
    </>
  );
};

const SessionSync: React.FC<{ setInitialState: InitialStateUpdater }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { tenants, currentTenantUuid } = useTenant();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );
  const currentTenantIsAdmin = React.useMemo(
    () => tenants.some((tenant) => tenant.id === currentTenantUuid && tenant.roleLabel === '管理员'),
    [currentTenantUuid, tenants],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === `${userSyncKey}|${currentTenantIsAdmin}`
    ) {
      return;
    }

    setInitialState((prev) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey &&
        prev.currentTenantIsAdmin === currentTenantIsAdmin
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        currentTenantIsAdmin,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey: `${userSyncKey}|${currentTenantIsAdmin}`,
    };
  }, [
    currentUser,
    currentTenantIsAdmin,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = navTheme === 'realDark' ? 'dark' : 'light';
    }

    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item: unknown) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item: unknown) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item: unknown) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: AppInitialState | undefined; setInitialState: InitialStateUpdater },
) => {
  if (typeof window !== 'undefined') {
    const storageKey = 'app_nav_theme';
    const storedTheme = window.localStorage.getItem(storageKey);
    if (storedTheme !== 'light' && storedTheme !== 'realDark') {
      window.localStorage.setItem(storageKey, 'light');
    }
    document.documentElement.dataset.theme = storedTheme === 'realDark' ? 'dark' : 'light';
  }

  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: InitialStateUpdater;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <App>
      <AntdAppBridge />
      <SessionProvider config={sessionConfig}>
        <SessionSync setInitialState={setInitialState} />
        <OpenPlatformBootstrap>
          {children}
        </OpenPlatformBootstrap>
      </SessionProvider>
    </App>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<AppInitialState> {
  if (hasOAuth2Session()) {
    const [dictionaryCatalogResult, authorizedResourcesResult, tenantListResult] = await Promise.allSettled([
      queryDictionaryCatalog(),
      getMyAuthorizedResources(),
      getBootstrapTenantList(),
    ]);

    if (dictionaryCatalogResult.status === 'fulfilled') {
      setDictionaryCatalog(dictionaryCatalogResult.value || []);
    } else {
      setDictionaryCatalog([]);
    }

    const authorizedResourceAccess =
      authorizedResourcesResult.status === 'fulfilled'
        ? buildAuthorizedResourceAccessMap(authorizedResourcesResult.value || [])
        : {};
    const tenantList = tenantListResult.status === 'fulfilled'
      ? tenantListResult.value || []
      : [];
    const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;

    return {
      loading: true,
      authorizedResourceAccess,
      currentTenantIsAdmin: tenantList.some(
        (tenant) => tenant.id === currentTenantUuid && tenant.roleLabel === '管理员',
      ),
      settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
        ProLayoutSettings & CoreLayoutSettings
      >,
    };
  } else {
    setDictionaryCatalog([]);
  }

  return {
    loading: true,
    authorizedResourceAccess: {},
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RuntimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const intl = useIntl();
  const translateMenuName = React.useCallback(
    (key: string) => intl.formatMessage({ id: key, defaultMessage: key }),
    [intl],
  );
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const authorizedResourceAccess = resolveAccess(initialState);
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? 208;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;
  const currentPathname = history.location.pathname;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    contentWidth: 'Fluid',
    breadcrumbRender: false,
    headerHeight: 44,
    token: {
      ...(initialState?.settings as Record<string, any> | undefined)?.token,
      header: {
        ...(initialState?.settings as Record<string, any> | undefined)?.token?.header,
        heightLayoutHeader: 44,
        colorBgHeader: 'var(--app-sider-background)',
        colorBgScrollHeader: 'var(--app-sider-background)',
      },
      pageContainer: {
        ...(initialState?.settings as Record<string, any> | undefined)?.token?.pageContainer,
        colorBgPageContainer: 'var(--app-content-background)',
      },
    },
    className: ['crm-shell', 'crm-content-theme', isProjectDetailPath(currentPathname) ? 'project-detail-layout' : '']
      .filter(Boolean)
      .join(' '),
    contentStyle: {
      height: 'calc(100dvh - 44px)',
      overflow: 'auto',
      minWidth: 0,
      padding: 0,
    },
    menuDataRender: (menuData: MenuDataItem[]) => {
      const projectDetailMenuData = getProjectDetailMenuData(
        currentPathname,
        authorizedResourceAccess,
        translateMenuName,
      );

      if (projectDetailMenuData) {
        return projectDetailMenuData;
      }
      return buildMenuTree(
        PROJECT_CENTER_MENU_CONFIGS,
        translateMenuName,
        authorizedResourceAccess,
      );
    },
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <CurrentTenant key="current-tenant" />,
        <OpenPlatformNotificationIcon key="notification" />,
        <Question key="doc" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return <Footer />;
    },
    headerContentRender: () => {
      if (isProjectDetailPath(currentPathname)) {
        return <ProjectDetailHeaderBanner pathname={currentPathname} />;
      }
      return null;
    },
    headerRender: (_props: unknown, dom: React.ReactNode) => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return false;
      }
      return dom;
    },
    menuRender: (_props: unknown, dom: React.ReactNode) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav) {
        return false;
      }
      return dom;
    },
    headerTitleRender: (logo: React.ReactNode) => (
      <HeaderTitle
        logo={logo}
        appTitle={appTitle}
        workbenchUrl={workbenchUrl}
      />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    siderWidth,
    childrenRender: (children: React.ReactNode) => {
      const { showNav } = getCurrentLayoutVisibility();
      const hideProjectDetailPageTitle = isProjectDetailPath(currentPathname);
      return (
        <>
          {hideProjectDetailPageTitle ? (
            <div className="project-detail-shell-page">{children}</div>
          ) : (
            children
          )}
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
